import React, { useState, useEffect, useCallback } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { RISKOMETER_API_URL } from '../../config';
import './RiskoMeter.css';

type RiskoMeterData = {
	No_of_Faces: number;
	No_of_Fire_Objects: number;
	No_of_Persons: number;
	No_of_Weapons: number;
	Risk_Factor: number;
};

type RiskoMeterProps = {
	mediaId: string;
	onClose: () => void;
};

function RiskoMeter({ mediaId, onClose }: RiskoMeterProps) {
	const [data, setData] = useState<RiskoMeterData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		setData(null);
		try {
			const response = await fetch(`${RISKOMETER_API_URL}${mediaId}`);
			if (!response.ok) throw new Error('Analysis failed');
			const result: RiskoMeterData = await response.json();
			setData(result);
		} catch (e: any) {
			setError(e.message || 'Something went wrong');
		} finally {
			setIsLoading(false);
		}
	}, [mediaId]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	// Close on Escape key
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, [onClose]);

	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) onClose();
	};

	// SVG gauge math — semi-circle arc from 170° to 10° (matching Flutter)
	const cx = 120,
		cy = 120,
		r = 90;
	const startAngleDeg = 170;
	const endAngleDeg = 10;
	const sweepDeg = (360 - startAngleDeg + endAngleDeg) % 360 || 360;
	const totalArc = (sweepDeg / 360) * 2 * Math.PI * r;

	const riskValue = data ? Math.round(data.Risk_Factor * 100) / 100 : 0;
	const fillFraction = data ? Math.min(data.Risk_Factor / 100, 1) : 0;
	const dashOffset = totalArc * (1 - fillFraction);

	// Compute arc path
	const toRad = (deg: number) => (deg * Math.PI) / 180;
	const startRad = toRad(startAngleDeg);
	const endRad = toRad(endAngleDeg);
	const x1 = cx + r * Math.cos(startRad);
	const y1 = cy + r * Math.sin(startRad);
	const x2 = cx + r * Math.cos(endRad);
	const y2 = cy + r * Math.sin(endRad);
	const largeArc = sweepDeg > 180 ? 1 : 0;
	const arcPath = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;

	const gradientId = `riskometer-gradient-${mediaId.replace(/[^a-zA-Z0-9]/g, '')}`;

	const stats = data
		? [
				{ label: 'Person Count', value: data.No_of_Persons },
				{ label: 'Faces Count', value: data.No_of_Faces },
				{ label: 'Fire/Fire Objects', value: data.No_of_Fire_Objects },
				{ label: 'Weapons Count', value: data.No_of_Weapons },
			]
		: [];

	return (
		<div className='riskometer-overlay' onClick={handleOverlayClick}>
			<div className='riskometer-card'>
				<button className='riskometer-close-btn' onClick={onClose}>
					<X size={18} />
				</button>

				<div className='riskometer-title'>Risko-Meter</div>

				{isLoading ? (
					<div className='riskometer-loading'>
						<div className='riskometer-spinner' />
						<span className='riskometer-loading-text'>Analyzing image…</span>
					</div>
				) : error ? (
					<div className='riskometer-error'>
						<AlertCircle className='riskometer-error-icon' />
						<span className='riskometer-error-text'>{error}</span>
						<button className='riskometer-retry-btn' onClick={fetchData}>
							Retry
						</button>
					</div>
				) : (
					<>
						<div className='riskometer-gauge-container'>
							<div className='riskometer-gauge-wrapper'>
								<svg className='riskometer-gauge-svg' viewBox='0 0 240 140'>
									<defs>
										<linearGradient id={gradientId} x1='0%' y1='0%' x2='100%' y2='0%'>
											<stop offset='25%' stopColor='#5308a3' />
											<stop offset='75%' stopColor='#3b82f6' />
										</linearGradient>
									</defs>
									<path d={arcPath} className='riskometer-gauge-bg' />
									<path
										d={arcPath}
										className='riskometer-gauge-fill'
										stroke={`url(#${gradientId})`}
										strokeDasharray={totalArc}
										strokeDashoffset={dashOffset}
									/>
								</svg>
								<div className='riskometer-gauge-value'>{riskValue}</div>
							</div>
							<div className='riskometer-scale'>
								<span>0</span>
								<span>50</span>
								<span>100</span>
							</div>
							<div className='riskometer-gauge-label'>Risk Factor</div>
						</div>

						<div className='riskometer-stats-grid'>
							{stats.map((stat) => (
								<div key={stat.label} className='riskometer-stat-card'>
									<div className='riskometer-stat-label'>{stat.label}</div>
									<div className='riskometer-stat-value'>{stat.value}</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	);
}

export default RiskoMeter;
